/**
 * Scandinavian / Swedish-Finnish (Windows KBDSW / Microsoft "Swedish"): maps a physical code to
 * the unshifted character that key emits. TheCore's "Scandinavia" is the SE/FI variant (a-ring,
 * a-diaeresis, o-diaeresis - NOT the Danish/Norwegian ae/o-slash), confirmed against the Microsoft
 * KBDSW reference: a-ring sits right of P, o-diaeresis and a-diaeresis right of L, with a "+" key and
 * a dead acute on the number row. Letters uppercase by table convention; accented letters lowercase.
 * Backquote (the section key), Equal (dead acute), BracketRight (dead diaeresis) are legends only.
 * The ISO key left of Z (IntlBackslash, "less-than") is outside the US-Qwerty code block and unbound
 * by TheCore. Source: Microsoft KBDSW (learn.microsoft.com/.../keyboards/kbdsw.html), base layer.
 */
export const SCANDINAVIAN_CODE_TO_CHAR: Record<string, string> = {
  Backquote: "§", // section sign (U+00A7), VK_OEM_5
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",
  Digit0: "0",
  Minus: "+",
  Equal: "´", // dead acute (U+00B4)
  KeyQ: "Q",
  KeyW: "W",
  KeyE: "E",
  KeyR: "R",
  KeyT: "T",
  KeyY: "Y",
  KeyU: "U",
  KeyI: "I",
  KeyO: "O",
  KeyP: "P",
  BracketLeft: "å", // a ring (U+00E5)
  BracketRight: "¨", // dead diaeresis (U+00A8)
  Backslash: "'", // apostrophe, ISO key left of Enter (VK_OEM_2)
  KeyA: "A",
  KeyS: "S",
  KeyD: "D",
  KeyF: "F",
  KeyG: "G",
  KeyH: "H",
  KeyJ: "J",
  KeyK: "K",
  KeyL: "L",
  Semicolon: "ö", // o diaeresis (U+00F6)
  Quote: "ä", // a diaeresis (U+00E4)
  KeyZ: "Z",
  KeyX: "X",
  KeyC: "C",
  KeyV: "V",
  KeyB: "B",
  KeyN: "N",
  KeyM: "M",
  Comma: ",",
  Period: ".",
  Slash: "-",
};

/**
 * The .SC2Hotkeys serialization token per code where the Scandinavian layout's token diverges from
 * charToken of its character. SC2 binds these OEM keys by virtual-key, so the
 * file names the key by its Swedish VK, not the symbol it emits: the "+" key serializes as "Equals"
 * (VK_OEM_PLUS), the dead-acute key as "BracketOpen" (VK_OEM_4), the a-ring key as "BracketClose"
 * (VK_OEM_6), and so on. Recovered from TheCore's real US-Qwerty -> Scandinavia pair (both hands) and
 * verified to reproduce those files command-for-command. Letters and digits are omitted: a letter
 * matches charToken(map[code]) via the char path, and a digit token is positional. The map is
 * injective so every key round-trips. Comma and Period serialize as themselves (char path) and are
 * omitted. Backquote's token (BackSlash, VK_OEM_5) is witnessed by the left-hand file (Hive/Lair,
 * Land, Lift, ...); the right-hand file leaves its one Backquote binding (ChatAllies) on "Grave"
 * unconverted - a documented TheCore source quirk.
 */
export const SCANDINAVIAN_CODE_TO_TOKEN: Record<string, string> = {
  Backquote: "BackSlash", // §, VK_OEM_5
  Minus: "Equals", // +, VK_OEM_PLUS
  Equal: "BracketOpen", // ´ dead key, VK_OEM_4
  BracketLeft: "BracketClose", // å, VK_OEM_6
  BracketRight: "SemiColon", // ¨ dead key, VK_OEM_1
  Backslash: "Slash", // ', VK_OEM_2
  Semicolon: "Grave", // ö, VK_OEM_3
  Quote: "Apostrophe", // ä, VK_OEM_7
  Slash: "Minus", // -, VK_OEM_MINUS
};
